import React, { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import {
  Zap, FileText, BarChart2, ClipboardList, Box, BookOpen,
  ArrowUpRight, ChevronLeft, ChevronRight,
} from "lucide-react";
import medhaSlide1 from "../assets/medha-screenshot.png";
import medhaSlide2 from "../assets/medha-slide-2.png";
import medhaSlide3 from "../assets/medha-slide-3.png";
import medhaSlide4 from "../assets/medha-slide-4.png";
import medhaSlide5 from "../assets/medha-slide-5.png";
import medhaSlide6 from "../assets/medha-slide-6.png";
import medhaIcon from "../assets/medha-icon.png";
import "./MedhaAISection.scss";
import { impact } from "../data/stats";
import AsOfNote from "./AsOfNote";

const SLIDES       = [medhaSlide1, medhaSlide2, medhaSlide3, medhaSlide4, medhaSlide5, medhaSlide6];
const DRIVE_EMBED  = "https://drive.google.com/file/d/1fzbCIbwCYDfhqb9fLpI14BljyznljXS3/preview";

function WindowBody() {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);
  const [current, setCurrent]   = useState(0);

  const next = useCallback(() => setCurrent(i => (i + 1) % SLIDES.length), []);
  const prev = useCallback(() => setCurrent(i => (i - 1 + SLIDES.length) % SLIDES.length), []);

  useEffect(() => {
    const fn = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, []);

  useEffect(() => {
    if (!isMobile) return;
    const id = setInterval(next, 3000);
    return () => clearInterval(id);
  }, [isMobile, next]);

  // ── Mobile: image carousel ────────────────────────────────────────────────
  if (isMobile) {
    return (
      <div className="mai__win-body mai__carousel">
        <div
          className="mai__carousel-track"
          style={{ transform: `translateX(-${current * 100}%)` }}
        >
          {SLIDES.map((src, i) => (
            <img
              key={i}
              src={src}
              alt={`Medha AI screen ${i + 1}`}
              className="mai__win-screenshot"
            />
          ))}
        </div>
        <button className="mai__car-btn mai__car-btn--prev" onClick={prev}>
          <ChevronLeft size={16} />
        </button>
        <button className="mai__car-btn mai__car-btn--next" onClick={next}>
          <ChevronRight size={16} />
        </button>
        <div className="mai__car-dots">
          {SLIDES.map((_, i) => (
            <button
              key={i}
              className={`mai__car-dot${i === current ? " mai__car-dot--active" : ""}`}
              onClick={() => setCurrent(i)}
            />
          ))}
        </div>
      </div>
    );
  }

  // ── Desktop: video embed ──────────────────────────────────────────────────
  return (
    <div className="mai__win-body mai__video">
      <iframe
        className="mai__video-frame"
        src={DRIVE_EMBED}
        allow="autoplay; fullscreen"
        allowFullScreen
        title="Medha AI Product Demo"
      />
    </div>
  );
}

export default function MedhaAISection() {
  return (
    <section className="mai-wrap">
      <div className="mai-inner">

        <motion.div
          className="mai__top"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.4, ease: "easeOut" }}
        >
          <div className="mai__chip">
            <img src={medhaIcon} alt="" className="mai__chip-icon" /> AI for Indian Schools
          </div>
          <h2 className="mai__h2">
            Meet <span>Medha AI</span> — built for<br />India's teachers
          </h2>
        </motion.div>

        <motion.div
          className="mai__showcase"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.4, delay: 0.08, ease: "easeOut" }}
        >
          <div className="mai__pill mai__pill--1"><Zap size={11} strokeWidth={2} /> Lesson Plans</div>
          <div className="mai__pill mai__pill--2"><ClipboardList size={11} strokeWidth={2} /> Activity Plans</div>
          <div className="mai__pill mai__pill--3"><FileText size={11} strokeWidth={2} /> Quizzes & Exams</div>
          <div className="mai__pill mai__pill--4"><BarChart2 size={11} strokeWidth={2} /> Class Analysis</div>
          <div className="mai__pill mai__pill--5"><Box size={11} strokeWidth={2} /> 3D Explorer</div>
          <div className="mai__pill mai__pill--6"><BookOpen size={11} strokeWidth={2} /> Live Assessment</div>

          <div className="mai__window">
            <div className="mai__win-bar">
              <span className="mai__win-dot mai__win-dot--r" />
              <span className="mai__win-dot mai__win-dot--y" />
              <span className="mai__win-dot mai__win-dot--g" />
              <span className="mai__win-url">medha.cograd.in</span>
            </div>

            <WindowBody />

            <div className="mai__win-footer">
              <span><strong>{impact.teachers.short}</strong> Teachers</span>
              <span className="mai__sep" />
              <span><strong>{impact.students.short}</strong> Students</span>
              <span className="mai__sep" />
              <span><strong>12+</strong> Languages</span>
              <span className="mai__sep mai__stat--hide-mobile" />
              <span className="mai__stat--hide-mobile"><strong>24/7</strong> Available</span>
            </div>
            <AsOfNote className="asof--center mai__asof" />
          </div>
        </motion.div>

        <motion.div
          className="mai__ctas"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.35, delay: 0.12, ease: "easeOut" }}
        >
          <a
            href="https://medha.cograd.in/"
            target="_blank"
            rel="noopener noreferrer"
            className="mai__btn mai__btn--primary"
          >
            Try Medha AI <ArrowUpRight size={15} strokeWidth={2.5} />
          </a>
        </motion.div>

      </div>
    </section>
  );
}
